<template>
  <v-app-bar color="background"
             :class="envIndicationClass"
             elevation="3">

    <v-app-bar-nav-icon v-if="!modelValue"
                        @click.stop="$emit('update:modelValue', !modelValue)">
    </v-app-bar-nav-icon>

    <v-app-bar-title>
      <nav-breadcrumbs v-if="$route.name !== 'dashboard' && !isMobile"></nav-breadcrumbs>
      <span v-else>AMV Connected</span>
    </v-app-bar-title>

    <v-spacer></v-spacer>

    <v-menu open-on-hover
            open-on-click
            open-delay="100">
      <template v-slot:activator="{ props }">
        <v-btn icon
               v-bind="props">
          <v-badge v-if="incomingMessageCount + incomingMachineAlertCount > 0"
                   :content="incomingMessageCount + incomingMachineAlertCount"
                   color="error">
            <v-icon>mdi-bell-outline</v-icon>
          </v-badge>
          <v-icon v-else>mdi-bell-outline</v-icon>
        </v-btn>
      </template>
      <v-list>
        <v-list-item to="/alerts/unread">
          <v-list-item-title :title="`In the last ${numberOfDaysLatestData} days`">
            {{ incomingMessageCount }}
            Messages not marked as done
          </v-list-item-title>
        </v-list-item>
        <v-list-item to="/alerts/machine-alerts">
          <v-list-item-title :title="`In the last ${numberOfDaysLatestData} days`">
            {{ incomingMachineAlertCount }}
            Machine alerts
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-menu open-on-hover
            open-on-click
            open-delay="100"
            :close-on-content-click="false">
      <template v-slot:activator="{ props }">
        <v-btn :icon="isAdmin ? isAdminViewEnabled ? 'mdi-account-supervisor' : 'mdi-account-switch' : 'mdi-account'"
               v-bind="props">
        </v-btn>
      </template>
      <v-list>
        <v-list-item :class="{ 'mb-2': isAdmin }">
          <v-list-item-title>{{ me?.name }}</v-list-item-title>
          <v-list-item-subtitle>{{ me?.username }}</v-list-item-subtitle>
        </v-list-item>
        <v-divider></v-divider>
        <v-list-item v-if="isAdmin"
                     prepend-icon="mdi-account-supervisor">
          <gql-autocomplete v-model="customer"
                            :query="customersQuery"
                            :tags="customersTags"
                            :compute-filter-func="(filter: string) => (filter || customer?.customerId) ?? ''"
                            class="mt-6"
                            style="width: 400px;"
                            density="compact"
                            label="Impersonate Customer"
                            item-title="name"
                            persistent-hint
                            clearable>
            <template v-slot:selection="{ item, index }">
              {{ item.raw?.name }} ({{ item.raw?.customerId }})
            </template>
            <template v-slot:item="{ item, props }">
              <v-list-item v-bind="props"
                           :title="item.raw?.name + ' (' + item.raw?.customerId + ')'">
              </v-list-item>
            </template>
          </gql-autocomplete>
        </v-list-item>
        <v-divider v-if="isAdmin"></v-divider>
        <v-list-item class="dense-menu-item-icon"
                     @click.prevent="logout()"
                     prepend-icon="mdi-logout"
                     value>
          <v-list-item-title>Logout</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- <v-btn icon @click="changeTheme">
      <v-icon :icon="theme.global.current.value.dark ? 'mdi-weather-night' : 'mdi-weather-sunny'"></v-icon>
    </v-btn> -->

  </v-app-bar>
</template>

<script setup lang="ts">
import { useLocalStorage } from "@vueuse/core";
import { useQuery } from "villus";
import { useDisplay, useTheme } from "vuetify";

defineProps<{
  modelValue: boolean;
}>();

const config = useRuntimeConfig();
const { logout, me, isAdmin } = useAuth();
const { isAdminViewEnabled, disableAdminView } = useLayoutOpts();
const theme = useTheme();
const display = useDisplay();
const isMobile = ref(display.mobile);
const { $dayjs } = useNuxtApp();
const { impersonateTenant, stopTenantImpersonation } = useImpersonateTenant();

function changeTheme() {
  theme.global.name.value = theme.global.current.value.dark ? 'amvCustomLightTheme' : 'dark';
}

const envIndicationClass = computed(() => {
  if (config.public.appEnv === 'dev') {
    return 'env-indicator dev';
  } else if (config.public.appEnv === 'local') {
    return 'env-indicator local';
  } else {
    return '';
  }
});

////////////  Notification tasks  ////////////
const incomingMessageCount = ref(0);
const incomingMachineAlertCount = ref(0);

// count unfinished messages
const { execute: executeNotificationMessageAndAlertCountQuery } = useQuery({
  query: graphql(`
    query GetNumberOfNewIncomingMessagesAndAlerts {
      incomingMessageCount: notificationTasks(
        where: {
          done: { neq: true }
          direction: { eq: D2C }
          type: { eq: MESSAGE }
          dispatchedTimestamp: { gte: "${$dayjs().subtract(numberOfDaysLatestData, 'days').toISOString()}"}
        }
      ) {
        totalCount
      }
      incomingMachineAlertCount: notificationTasks(
        where: {
          done: { neq: true }
          direction: { eq: D2C }
          type: { eq: ALERT }
          dispatchedTimestamp: { gte: "${$dayjs().subtract(numberOfDaysLatestData, 'days').toISOString()}"}
        }
      ) {
        totalCount
      }
    }
  `),
  onData: (data) => {
    incomingMessageCount.value = data?.incomingMessageCount?.totalCount ?? 0;
    incomingMachineAlertCount.value = data?.incomingMachineAlertCount?.totalCount ?? 0;
  },
  cachePolicy: 'network-only',
});

useEventOn('notificationTask:upserted', (_) => {
  // We simply refetch the query to update the count instead of trying to check the subscription data
  executeNotificationMessageAndAlertCountQuery();
});

interface Customer {
  id: string;
  name: string;
  customerId: string;
  tenantId: string;
}

const customer = useLocalStorage<Customer | null>('amv-connected_impersonated-customer', null, {
  serializer: {
    read: (v: any) => v ? JSON.parse(v) : null,
    write: (v: any) => JSON.stringify(v),
  }
});
const customersTags = ['customers'];
const customersQuery = graphql(`
  query GetCustomers($first: Int! $after: String $search: String) {
    customers(
      order: { name: ASC }
      first: $first
      after: $after
      where: {
        or: [
          { customerId: { contains: $search } }
          { name: { contains: $search } }
        ]
        and: {
          tenantId: { neq: null }
          isActive: { eq: true }
        }
      }
    ) {
      edges {
        node {
          id
          name
          customerId
          tenantId
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
      }
    }
  }
`);

if (customer.value !== null) {
  disableAdminView();
}

watch(customer, (value) => {
  if (value) {
    impersonateTenant(value.tenantId);
  } else {
    stopTenantImpersonation();
  }
});

</script>

<style scoped lang="scss">
.env-indicator {
  background-color: #E3F2FD !important; //blue-lighten-5

  &:after {
    margin-left: 35vw;
    position: absolute;
    color: rgb(180, 180, 180);
    z-index: -1;
  }

  &.local:after {
    content: "LOCAL";
  }

  &.dev:after {
    content: "DEV";
  }
}
</style>
